// The header's top menu, and who sees it (#43).
//
// THE MENU IS FOR ADMINS AND THE PEOPLE AN ADMIN NAMES. An Admin gives somebody
// the Job flag on the Admin page, and `/api/me` reports it as
// `can_use_job_menu`, true for every Admin by the role
// (entsys-barriertracker-api#43). Everybody else with access keeps the rail.
//
// THE SCREENS BEHIND IT ARE GUARDED TOO, not only the links, so an address
// typed or bookmarked does not open them: `JobOnly` sends anybody without the
// flag to their landing view, as `AdminOnly` does for the Admin page.
//
// RECORDS STAY OPEN TO EVERYONE. HOT, the dashboards, the barrier lists and Two
// Week Production all open, edit and create records through `/data/<table>/...`,
// so only the table index and a table's list are behind the flag.
//
// IT IS WHAT THE CLIENT SHOWS, NOT THE CONTROL. The API serves the menu's data
// to every User, because the rail's Dashboard reads orders and daily
// production too.

import type { ReactNode } from 'react';
import { NavLink, Navigate } from 'react-router-dom';
import type { Async } from '../data';
import type { Me } from '../types';

// The source's reference views, which it reaches from other views rather than
// from its navigation, and the tables. The navigation itself is the rail.
//
// `Interplant Transfers` is here even though the application opens on it. In
// the source it is a *reference* view and also the Starting view, so a
// faithful rail does not carry it.
export const MENU = [
  { to: '/interplant-transfers', label: 'Interplant Transfers' },
  { to: '/daily-production', label: 'Daily Production' },
  { to: '/orders', label: 'Orders' },
  { to: '/data', label: 'Tables' },
];

/** Whether the signed-in person sees the top menu and the screens behind it. */
export function mayUseJobMenu(me: Me | null | undefined): boolean {
  return Boolean(me?.is_admin || me?.can_use_job_menu);
}

/** Where the application opens. The source opens on Interplant Transfers,
 *  which is behind the menu; anybody who cannot open it lands on the Dashboard. */
export function landingFor(me: Me | null | undefined): string {
  return mayUseJobMenu(me) ? '/interplant-transfers' : '/dashboard';
}

/** The menu's links, or none. The `nav` stays either way: it is what holds
 *  the signed-in name to the right of the header. */
export function JobMenu({ me }: { me: Me | null | undefined }) {
  return (
    <nav className="app-nav">
      {mayUseJobMenu(me) &&
        MENU.map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? 'active' : undefined)}>
            {item.label}
          </NavLink>
        ))}
    </nav>
  );
}

/** A route's guard for the screens behind the menu. */
export function JobOnly({ me, children }: { me: Async<Me>; children: ReactNode }) {
  if (mayUseJobMenu(me.data)) return <>{children}</>;
  if (me.loading && !me.error) return <p className="muted">Loading…</p>;
  return <Navigate to={landingFor(me.data)} replace />;
}
